/**
 * School Administrator Dashboard
 * 
 * Displays all lesson launches across all classes, student quiz scores,
 * and curriculum completion levels for their school.
 */

import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, orderBy, onSnapshot, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import type { StudentScore, LessonLaunch, Class } from '../../types/lms';
import { Link } from 'react-router-dom';
import { learnXRFontStyle, TrademarkSymbol } from '../../Components/LearnXRTypography';
import { SchoolCodeBlock } from '../../Components/SchoolCodeBlock';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../Components/ui/card';
import { Button } from '../../Components/ui/button';
import { Badge } from '../../Components/ui/badge';
import { Progress } from '../../Components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../Components/ui/select';
import {
  FaSchool,
  FaChalkboardTeacher,
  FaGraduationCap,
  FaArrowRight,
  FaBook,
  FaClock,
  FaFilter,
  FaBell,
  FaCheckCircle,
  FaHourglassHalf,
  FaClipboardList,
  FaFileAlt,
} from 'react-icons/fa';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
// Shared with the teacher and principal dashboards so one class cannot show
// three different averages depending on who is looking.
import { SchoolPerformanceSection } from '../../Components/analytics/SchoolPerformanceSection';

interface CurriculumCompletion {
  curriculum: string;
  completedTopics: number;
  totalTopics: number;
  completionPercentage: number;
}

const SchoolDashboard = () => {
  const { user, profile } = useAuth();
  const [teachers, setTeachers] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [pendingStudents, setPendingStudents] = useState<any[]>([]);
  const [pendingTeachers, setPendingTeachers] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [scores, setScores] = useState<StudentScore[]>([]);
  const [launches, setLaunches] = useState<LessonLaunch[]>([]);
  const [chapters, setChapters] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  /*
    True once the first snapshot has landed.

    The page used to render a full-screen spinner whenever `loading` was true,
    and every re-subscribe set it true again — so a write to the user document
    replaced a correct, populated dashboard with a spinner. After the first load
    there is always something worth showing, and live updates land in place.
  */
  const [hasLoaded, setHasLoaded] = useState(false);

  // Latches on the first completed load and never unlatches, so a later refresh
  // can never put the page back behind a spinner.
  useEffect(() => {
    if (!loading) setHasLoaded(true);
  }, [loading]);

  /*
    Why a read failed, when one does.

    The error handlers here logged to the console and stopped. Both of these
    queries needed a composite index that did not exist — an equality filter with
    an orderBy on another field always does — so the page rendered zeros
    indefinitely and looked exactly like a school where nobody had taken a quiz.
  */
  const [dataError, setDataError] = useState<string | null>(null);
  const [selectedClassFilter, setSelectedClassFilter] = useState<string>('all');
  const [selectedCurriculumFilter, setSelectedCurriculumFilter] = useState<string>('all');
  const [schoolCode, setSchoolCode] = useState<string | null>(null);
  const [stats, setStats] = useState({
    totalClasses: 0,
    totalTeachers: 0,
    approvedTeachers: 0,
    pendingTeachersCount: 0,
    totalStudents: 0,
    approvedStudents: 0,
    pendingStudentsCount: 0,
    averageSchoolScore: 0,
    totalLessonLaunches: 0,
    completedLessons: 0,
    averageTimePerLesson: 0,
    totalLearningTime: 0,
  });

  // Fetch pending students and teachers
  useEffect(() => {
    if (!user?.uid || !profile || profile.role !== 'school') {
      console.warn('⚠️ SchoolDashboard: Missing required data', {
        hasUser: !!user?.uid,
        hasProfile: !!profile,
        role: profile?.role,
      });
      return;
    }

    // Use fallback: school_id or managed_school_id (same as SchoolApprovals)
    const schoolId = profile.school_id || profile.managed_school_id;
    
    if (!schoolId) {
      console.warn('⚠️ SchoolDashboard: No school_id or managed_school_id found', {
        profileSchoolId: profile.school_id,
        profileManagedSchoolId: profile.managed_school_id,
        profileRole: profile.role,
        profileUid: profile.uid,
      });
      return;
    }

    console.log('🔍 SchoolDashboard: Starting queries', {
      schoolId,
      profileSchoolId: profile.school_id,
      profileManagedSchoolId: profile.managed_school_id,
      adminUid: user.uid,
    });

    // Fetch all pending students in the school
    const pendingStudentsQuery = query(
      collection(db, 'users'),
      where('role', '==', 'student'),
      where('school_id', '==', schoolId),
      where('approvalStatus', '==', 'pending')
    );

    const unsubscribePendingStudents = onSnapshot(pendingStudentsQuery, (snapshot) => {
      const pendingData = snapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data(),
      }));
      
      console.log('🔍 SchoolDashboard: Pending students query result', {
        total: pendingData.length,
        schoolId,
        students: pendingData.map(s => ({
          uid: s.uid,
          name: s.name || s.displayName,
          school_id: s.school_id,
          approvalStatus: s.approvalStatus,
        })),
      });
      
      setPendingStudents(pendingData);
    }, (error) => {
      console.error('❌ SchoolDashboard: Error fetching pending students', {
        error,
        errorObject: error,
        schoolId,
        errorCode: error.code,
        errorMessage: error.message,
        errorStack: error.stack,
        profileRole: profile.role,
        profileSchoolId: profile.school_id,
        profileManagedSchoolId: profile.managed_school_id,
      });
    });

    // Fetch all pending teachers in the school
    const pendingTeachersQuery = query(
      collection(db, 'users'),
      where('role', '==', 'teacher'),
      where('school_id', '==', schoolId),
      where('approvalStatus', '==', 'pending')
    );

    const unsubscribePendingTeachers = onSnapshot(pendingTeachersQuery, (snapshot) => {
      const pendingData = snapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data(),
      }));
      
      console.log('🔍 SchoolDashboard: Pending teachers query result', {
        total: pendingData.length,
        schoolId,
        teachers: pendingData.map(t => ({
          uid: t.uid,
          name: t.name || t.displayName,
          school_id: t.school_id,
          approvalStatus: t.approvalStatus,
        })),
      });
      
      setPendingTeachers(pendingData);
    }, (error) => {
      console.error('❌ SchoolDashboard: Error fetching pending teachers', {
        error,
        schoolId,
        errorCode: error.code,
        errorMessage: error.message,
      });
    });

    return () => {
      unsubscribePendingStudents();
      unsubscribePendingTeachers();
    };
  }, [user?.uid, profile]);

  // Fetch school code for display
  useEffect(() => {
    const schoolId = profile?.school_id || profile?.managed_school_id;
    if (!schoolId) return;
    getDoc(doc(db, 'schools', schoolId))
      .then((snap) => {
        if (snap.exists()) setSchoolCode(snap.data()?.schoolCode || null);
      })
      .catch(() => {});
  }, [profile?.school_id, profile?.managed_school_id]);

  useEffect(() => {
    if (!user?.uid || !profile || profile.role !== 'school') return;

    // Use fallback: school_id or managed_school_id (same as SchoolApprovals)
    const schoolId = profile.school_id || profile.managed_school_id;
    
    if (!schoolId) {
      console.warn('⚠️ SchoolDashboard: No school_id or managed_school_id found for school admin', {
        profileRole: profile.role,
        profileSchoolId: profile.school_id,
        profileManagedSchoolId: profile.managed_school_id,
        profileUid: profile.uid,
      });
      setLoading(false);
      return;
    }

    console.log('🔍 SchoolDashboard: Starting queries', {
      schoolId,
      profileRole: profile.role,
      profileSchoolId: profile.school_id,
      profileManagedSchoolId: profile.managed_school_id,
    });

    setLoading(true);

    // Get all classes in school
    const classesQuery = query(
      collection(db, 'classes'),
      where('school_id', '==', schoolId)
    );

    const unsubscribeClasses = onSnapshot(classesQuery, (snapshot) => {
      const classesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      console.log('🔍 SchoolDashboard: Classes query result', {
        total: classesData.length,
        schoolId,
      });
      setClasses(classesData);
    }, (error) => {
      console.error('❌ SchoolDashboard: Error fetching classes', {
        error,
        schoolId,
        errorCode: error.code,
        errorMessage: error.message,
      });
    });

    // Get all teachers in school
    const teachersQuery = query(
      collection(db, 'users'),
      where('role', '==', 'teacher'),
      where('school_id', '==', schoolId)
    );

    const unsubscribeTeachers = onSnapshot(teachersQuery, (snapshot) => {
      const teachersData = snapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data(),
      }));
      console.log('🔍 SchoolDashboard: Teachers query result', {
        total: teachersData.length,
        schoolId,
        teachers: teachersData.map(t => ({
          uid: t.uid,
          name: t.name || t.displayName,
          school_id: t.school_id,
          approvalStatus: t.approvalStatus,
        })),
      });
      setTeachers(teachersData);
    }, (error) => {
      console.error('❌ SchoolDashboard: Error fetching teachers', {
        error,
        schoolId,
        errorCode: error.code,
        errorMessage: error.message,
        profileRole: profile.role,
        profileSchoolId: profile.school_id,
        profileManagedSchoolId: profile.managed_school_id,
      });
    });

    // Get all students in school
    const studentsQuery = query(
      collection(db, 'users'),
      where('role', '==', 'student'),
      where('school_id', '==', schoolId)
    );

    const unsubscribeStudents = onSnapshot(studentsQuery, (snapshot) => {
      const studentsData = snapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data(),
      }));
      console.log('🔍 SchoolDashboard: Students query result', {
        total: studentsData.length,
        schoolId,
        students: studentsData.map(s => ({
          uid: s.uid,
          name: s.name || s.displayName,
          school_id: s.school_id,
          approvalStatus: s.approvalStatus,
          class_ids: s.class_ids,
        })),
      });
      setStudents(studentsData);
    }, (error) => {
      console.error('❌ SchoolDashboard: Error fetching students', {
        error,
        schoolId,
        errorCode: error.code,
        errorMessage: error.message,
        profileRole: profile.role,
        profileSchoolId: profile.school_id,
        profileManagedSchoolId: profile.managed_school_id,
      });
    });

    // Get all scores in school
    const scoresQuery = query(
      collection(db, 'student_scores'),
      where('school_id', '==', schoolId),
      orderBy('completed_at', 'desc')
    );

    const unsubscribeScores = onSnapshot(scoresQuery, (snapshot) => {
      const scoresData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as StudentScore[];
      console.log('🔍 SchoolDashboard: Scores query result', {
        total: scoresData.length,
        schoolId,
      });
      setScores(scoresData);
      setDataError(null);
    }, (error) => {
      setDataError(`Student scores could not be read: ${error.message}`);
      console.error('❌ SchoolDashboard: Error fetching scores', {
        error,
        schoolId,
        errorCode: error.code,
        errorMessage: error.message,
      });
    });

    // Get all lesson launches in school
    const launchesQuery = query(
      collection(db, 'lesson_launches'),
      where('school_id', '==', schoolId),
      orderBy('launched_at', 'desc')
    );

    const unsubscribeLaunches = onSnapshot(launchesQuery, (snapshot) => {
      const launchesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as LessonLaunch[];
      console.log('🔍 SchoolDashboard: Launches query result', {
        total: launchesData.length,
        schoolId,
      });
      setLaunches(launchesData);
    }, (error) => {
      setDataError(`Lesson launches could not be read: ${error.message}`);
      console.error('❌ SchoolDashboard: Error fetching launches', {
        error,
        schoolId,
        errorCode: error.code,
        errorMessage: error.message,
      });
    });

    // Fetch all chapters for curriculum completion calculation
    const fetchChapters = async () => {
      try {
        const chaptersRef = collection(db, 'curriculum_chapters');
        const chaptersSnapshot = await getDocs(chaptersRef);
        const chaptersData = chaptersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setChapters(chaptersData);
      } catch (error) {
        console.error('Error fetching chapters:', error);
      }
    };

    fetchChapters();

    // Cleanup
    return () => {
      unsubscribeClasses();
      unsubscribeTeachers();
      unsubscribeStudents();
      unsubscribeScores();
      unsubscribeLaunches();
    };
  }, [user?.uid, profile]);

  // Calculate curriculum completion
  const curriculumCompletion = useMemo((): CurriculumCompletion[] => {
    if (chapters.length === 0) return [];

    const curriculumMap = new Map<string, { completed: number; total: number }>();

    chapters.forEach(chapter => {
      const curriculum = chapter.curriculum || 'UNKNOWN';
      if (!curriculumMap.has(curriculum)) {
        curriculumMap.set(curriculum, { completed: 0, total: 0 });
      }

      const curriculumData = curriculumMap.get(curriculum)!;
      
      if (chapter.topics && Array.isArray(chapter.topics)) {
        chapter.topics.forEach(topic => {
          curriculumData.total++;
          // Check if topic is completed by any student in the school
          const isCompleted = launches.some(launch => 
            launch.chapter_id === chapter.id &&
            launch.topic_id === topic.topic_id &&
            launch.completion_status === 'completed'
          );
          if (isCompleted) {
            curriculumData.completed++;
          }
        });
      }
    });

    return Array.from(curriculumMap.entries()).map(([curriculum, data]) => ({
      curriculum,
      completedTopics: data.completed,
      totalTopics: data.total,
      completionPercentage: data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0,
    })).sort((a, b) => b.completionPercentage - a.completionPercentage);
  }, [chapters, launches]);

  // Calculate time tracking metrics
  const timeMetrics = useMemo(() => {
    const totalTime = scores.reduce((sum, s) => sum + (s.time_taken_seconds || 0), 0) +
                     launches.reduce((sum, l) => sum + (l.duration_seconds || 0), 0);
    const completedLaunches = launches.filter(l => l.completion_status === 'completed');
    const averageTimePerLesson = completedLaunches.length > 0
      ? totalTime / completedLaunches.length
      : 0;

    return {
      totalLearningTime: totalTime, // in seconds
      averageTimePerLesson: Math.round(averageTimePerLesson), // in seconds
      totalLessons: launches.length,
      completedLessons: completedLaunches.length,
    };
  }, [scores, launches]);

  // Update stats
  useEffect(() => {
    const totalClasses = classes.length;
    const totalTeachers = teachers.length;
    const approvedTeachers = teachers.filter(t => t.approvalStatus === 'approved').length;
    // Use pendingTeachers state instead of filtering teachers array (more accurate)
    const pendingTeachersCount = pendingTeachers.length;
    const totalStudents = students.length;
    const approvedStudents = students.filter(s => s.approvalStatus === 'approved').length;
    // Use pendingStudents state instead of filtering students array (more accurate)
    const pendingStudentsCount = pendingStudents.length;
    const averageSchoolScore = scores.length > 0
      ? scores.reduce((sum, s) => sum + (s.score?.percentage || 0), 0) / scores.length
      : 0;
    const totalLessonLaunches = launches.length;
    const completedLessons = launches.filter(l => l.completion_status === 'completed').length;

    setStats({
      totalClasses,
      totalTeachers,
      approvedTeachers,
      pendingTeachersCount,
      totalStudents,
      approvedStudents,
      pendingStudentsCount,
      averageSchoolScore: Math.round(averageSchoolScore),
      totalLessonLaunches,
      completedLessons,
      averageTimePerLesson: timeMetrics.averageTimePerLesson,
      totalLearningTime: timeMetrics.totalLearningTime,
    });
    setLoading(false);
  }, [classes, teachers, students, scores, launches, timeMetrics, pendingTeachers, pendingStudents]);

  // Only before anything has ever been shown. A refresh over existing
  // content must not blank the page.
  if (loading && !hasLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary/30 border-t-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading school dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 pb-6 border-b border-border">
          <div className="flex items-start justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-primary/10 border border-border flex items-center justify-center">
                <FaSchool className="text-primary text-xl" />
              </div>
              <div>
                <h1 className="text-3xl font-bold mb-1" style={learnXRFontStyle}>
                  <span className="text-foreground">In</span>
                  <span className="text-primary">3D.ai</span>
                  <TrademarkSymbol />
                </h1>
                <h2 className="text-xl font-semibold text-foreground">School Administrator Dashboard</h2>
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  <p className="text-muted-foreground text-sm">Monitor lesson launches, quiz scores, and curriculum completion</p>
                  {schoolCode && (
                    <SchoolCodeBlock code={schoolCode} variant="dashboard" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-8 flex gap-4 items-center flex-wrap">
          <div className="flex items-center gap-2">
            <FaFilter className="text-muted-foreground" />
            <span className="text-muted-foreground text-sm">Filters:</span>
          </div>
          <Select value={selectedClassFilter} onValueChange={setSelectedClassFilter}>
            <SelectTrigger className="w-[200px] h-10 border border-input bg-background text-foreground">
              <SelectValue placeholder="All Classes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Classes</SelectItem>
              {classes.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.class_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedCurriculumFilter} onValueChange={setSelectedCurriculumFilter}>
            <SelectTrigger className="w-[200px] h-10 border border-input bg-background text-foreground">
              <SelectValue placeholder="All Curricula" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Curricula</SelectItem>
              {Array.from(new Set(chapters.map(c => c.curriculum))).map(cur => (
                <SelectItem key={cur} value={cur}>{cur}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Quick action tiles */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
          <Link
            to="/question-paper/generate"
            className="rounded-xl border border-border bg-card p-4 hover:border-primary/60 transition"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 border border-border flex items-center justify-center">
                <FaClipboardList className="text-primary" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold">AI Question Paper</div>
                <div className="text-xs text-muted-foreground truncate">Generate CBSE/RBSE papers for your school</div>
              </div>
            </div>
          </Link>
          <Link
            to="/question-paper/library"
            className="rounded-xl border border-border bg-card p-4 hover:border-primary/60 transition"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 border border-border flex items-center justify-center">
                <FaFileAlt className="text-primary" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold">Paper Library</div>
                <div className="text-xs text-muted-foreground truncate">Browse saved question papers</div>
              </div>
            </div>
          </Link>
          <Link
            to="/admin/classes?tab=teachers"
            className="rounded-xl border border-border bg-card p-4 hover:border-primary/60 transition"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 border border-border flex items-center justify-center">
                <FaCheckCircle className="text-primary" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold">Approvals</div>
                <div className="text-xs text-muted-foreground truncate">Review teacher requests</div>
              </div>
            </div>
          </Link>
        </section>

        {/* School Academic Performance */}
        {(profile?.school_id || profile?.managed_school_id) && (
          <div className="mb-8">
            <SchoolPerformanceSection
              schoolId={(profile.school_id || profile.managed_school_id)!}
              classes={classes}
              students={students}
              teachers={teachers}
              scores={scores}
              loading={loading && !hasLoaded}
              error={dataError}
              canEditThreshold
            />
          </div>
        )}

        {/* Time Tracking Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <Card className="border border-border bg-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 border border-border flex items-center justify-center">
                  <FaClock className="text-primary text-xl" />
                </div>
                <div>
                  <p className="text-muted-foreground text-sm">Avg Time/Lesson</p>
                  <p className="text-2xl font-bold text-foreground">{Math.round(stats.averageTimePerLesson / 60)} min</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-border bg-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 border border-border flex items-center justify-center">
                  <FaHourglassHalf className="text-primary text-xl" />
                </div>
                <div>
                  <p className="text-muted-foreground text-sm">Total Learning Time</p>
                  <p className="text-2xl font-bold text-foreground">{Math.round(stats.totalLearningTime / 3600)} hrs</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Access Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <Card className="border border-border bg-card">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-lg bg-primary/10 border border-border flex items-center justify-center">
                    <FaBook className="text-primary text-2xl" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground">Class Management</h2>
                    <p className="text-muted-foreground text-sm mt-1">Manage classes and assign teachers.</p>
                  </div>
                </div>
                <Button asChild variant="secondary" className="gap-2 shrink-0">
                  <Link to="/admin/classes">Open <FaArrowRight className="w-4 h-4" /></Link>
                </Button>
              </div>
            </CardContent>
          </Card>
          <Card className={`border bg-card ${pendingTeachers.length > 0 ? 'border-amber-500/40' : 'border-border'}`}>
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-lg bg-primary/10 border border-border flex items-center justify-center relative">
                    <FaChalkboardTeacher className="text-primary text-2xl" />
                    {pendingTeachers.length > 0 && (
                      <Badge className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0 flex items-center justify-center bg-destructive text-destructive-foreground">
                        {pendingTeachers.length}
                      </Badge>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-semibold text-foreground">Teacher Approvals</h2>
                      {pendingTeachers.length > 0 && (
                        <Badge variant="secondary" className="gap-1">
                          <FaBell className="text-xs" /> {pendingTeachers.length} pending
                        </Badge>
                      )}
                    </div>
                    <p className="text-muted-foreground text-sm mt-1">
                      {pendingTeachers.length > 0 ? `${pendingTeachers.length} teacher(s) waiting.` : 'Approve teachers for your school.'}
                    </p>
                  </div>
                </div>
                <Button asChild variant={pendingTeachers.length > 0 ? 'default' : 'secondary'} className="gap-2 shrink-0">
                  <Link to="/admin/classes?tab=teachers">
                    {pendingTeachers.length > 0 ? 'Review' : 'Open'}
                    <FaArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Curriculum Completion */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4">Curriculum Completion</h2>
          {curriculumCompletion.length === 0 ? (
            <Card className="border border-border bg-card">
              <CardContent className="p-8 text-center">
                <FaGraduationCap className="text-4xl text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No curriculum data available</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {curriculumCompletion.map((curriculum) => (
                <Card key={curriculum.curriculum} className="border border-border bg-card hover:bg-accent/30 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-foreground font-semibold text-lg">{curriculum.curriculum}</h3>
                      <span className={`text-2xl font-bold ${
                        curriculum.completionPercentage >= 70 ? 'text-primary' :
                        curriculum.completionPercentage >= 50 ? 'text-amber-500' : 'text-destructive'
                      }`}>
                        {curriculum.completionPercentage}%
                      </span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Completed Topics</span>
                        <span className="text-foreground">{curriculum.completedTopics}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Total Topics</span>
                        <span className="text-foreground">{curriculum.totalTopics}</span>
                      </div>
                      <Progress value={curriculum.completionPercentage} className="h-2 mt-3" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Recent Lesson Launches */}
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-4">Recent Lesson Launches</h2>
          {launches.length === 0 ? (
            <Card className="border border-border bg-card">
              <CardContent className="p-8 text-center">
                <FaGraduationCap className="text-4xl text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No lesson launches yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {launches.slice(0, 10).map((launch) => {
                const student = students.find(s => s.uid === launch.student_id);
                return (
                  <Card key={launch.id} className="border border-border bg-card hover:bg-accent/30 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="text-foreground font-medium">
                            {student?.name || student?.displayName || 'Unknown Student'}
                          </h3>
                          <p className="text-muted-foreground text-sm mt-1">
                            {launch.curriculum} Class {launch.class_name} • {launch.subject}
                          </p>
                          <p className="text-muted-foreground/70 text-xs mt-1">
                            Chapter: {launch.chapter_id} • Topic: {launch.topic_id}
                          </p>
                        </div>
                        <div className="ml-4 text-right">
                          <Badge variant={launch.completion_status === 'completed' ? 'default' : 'secondary'} className="text-sm">
                            {launch.completion_status === 'completed' ? 'Completed' : 'In Progress'}
                          </Badge>
                          <p className="text-muted-foreground text-xs mt-1">
                            {launch.launched_at?.toDate?.()?.toLocaleDateString() || 'Unknown date'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchoolDashboard;
